import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import { Component } from 'react';
import { parse } from 'query-string';
import { History, Location } from 'history';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent } from 'Src/models/event';
import { getStudentEvent, getStudentEvents, sentStudentAvailabilityReport } from 'Src/helpers/service/student/events';
import {
  getActionList,
  getNextStudentEventFilter,
  getPreviousStudentEventFilter
} from 'Src/helpers/event/schoolEventViewHelper';
import { Loader } from 'Src/components/Loader/Loader';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { StudentEventMainInformation } from './MainInformation/MainInformation';
import { StudentSummaryTabs } from './SummaryTabs/SummaryTabs';
import { AvailabilityReportForm } from './Forms/AvailabilityReportForm';
import { EventActionButtonCallbacks } from '../../AdminView/TeamsAndEvents/Events/SchoolEventView/SchoolEventView';
import { Map } from 'Src/components/Map/Map';
import { getNewPoint } from 'Src/helpers/venue/venue';
import '../../AdminView/TeamsAndEvents/Events/SchoolEventView/SchoolEventPageShell.scss';
import { getEventGeneratedName } from 'Src/helpers/accessor/accessor';
import { EVENT_ACTION_BUTTONS_MAPPING } from 'Src/consts/event';
import { Report } from 'Src/models/report';
import { TABS } from 'Src/consts/common';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  setNeedUpdate?: () => void;
}

interface State {
  isLoading: boolean;
  event: SchoolEvent;
  prevEvent: SchoolEvent;
  nextEvent: SchoolEvent;
  isAvailabilityFormModalOpen: boolean;
  isMapModalOpen: boolean;
  isSuccessModalOpen: boolean;
  isErrorModalOpen: boolean;
  errorMessage: string;
  eventContentVersion: number;
  selectedAvailabilityReport?: Report;
  eventTabToOpen?: string;
}

export class StudentEventView extends Component<Props, State> {
  private loadRequestId = 0;
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      event: undefined,
      prevEvent: undefined,
      nextEvent: undefined,
      isAvailabilityFormModalOpen: false,
      isMapModalOpen: false,
      isSuccessModalOpen: false,
      isErrorModalOpen: false,
      errorMessage: '',
      eventContentVersion: 0,
      selectedAvailabilityReport: undefined,
      eventTabToOpen: undefined
    };
  }

  getEventId(props: Props = this.props): string {
    const { history } = props;
    const search = parse(history.location.search);
    const eventId = search.id;
    return eventId;
  }

  //reload component after change eventId by navigation buttons
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { location } = this.props;
    const { location: nextLocation } = nextProps;
    const { search } = location;
    const { search: nextSearch } = nextLocation;

    if (search !== nextSearch) {
      this.setState({
        isLoading: true
      });
      this.load(nextProps);
    }
  }

  load(props: Props = this.props) {
    const requestId = ++this.loadRequestId;
    const { user, history, location } = props;
    const eventId = this.getEventId(props);

    const calendarFilterType = propz.get(location, ['state', 'filter']);

    let event, prevEvent, nextEvent;

    // 1. Get current event
    // 2. Get previous/next events (for navigation)
    getStudentEvent(user, eventId)
      .then(_event => {
        event = _event;

        const currentEventStartTime = new Date(_event.startTime);

        const nextEventFilter = getNextStudentEventFilter(eventId, currentEventStartTime, user, calendarFilterType);
        const prevEventFilter = getPreviousStudentEventFilter(eventId, currentEventStartTime, user, calendarFilterType);

        const prevEventPromise = getStudentEvents(user, prevEventFilter);
        const nextEventPromise = getStudentEvents(user, nextEventFilter);

        return BPromise.all([prevEventPromise, nextEventPromise]);
      })
      .then(([_prevEvent, _nextEvent]) => {
        if (requestId !== this.loadRequestId) return;
        [prevEvent] = _prevEvent;
        [nextEvent] = _nextEvent;

        this.setState({
          isLoading: false,
          event,
          nextEvent,
          prevEvent,
          eventTabToOpen: propz.get(location, ['state', 'initialTab'])
        });
      })
      .catch(error => {
        if (requestId !== this.loadRequestId) return;
        console.error(error);
        const status = propz.get(error, ['response', 'status']);
        if (status === 403 || status === 404) {
          history.push({ pathname: '/calendar' });
          return;
        }
        this.setState({
          isLoading: false,
          isErrorModalOpen: true,
          errorMessage:
            propz.get(error, ['response', 'data', 'details', 'text']) ||
            'The event could not be loaded. Please try again.'
        });
      });
  }

  componentDidMount() {
    this.setState({
      isLoading: true
    });

    this.load();
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (this.state.event && this.state.event !== prevState.event) {
      const eventName = getEventGeneratedName(this.state.event, { user: this.props.user });
      window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: eventName }));
    }
  }

  componentWillUnmount() {
    this.loadRequestId += 1;
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
  }

  onPrevEventClick = () => {
    const { prevEvent } = this.state;
    const { history, location } = this.props;
    const { id } = prevEvent;

    const state = propz.get(location, ['state'], {});

    history.push({
      pathname: '/events/event',
      search: `id=${id}`,
      state
    });
  };

  onNextEventClick = () => {
    const { nextEvent } = this.state;
    const { history, location } = this.props;
    const { id } = nextEvent;

    const state = propz.get(location, ['state'], {});

    history.push({
      pathname: '/events/event',
      search: `id=${id}`,
      state
    });
  };

  openAvailabilityModal = (report?: Report) => {
    this.setState({
      isAvailabilityFormModalOpen: true,
      selectedAvailabilityReport: report
    });
  };

  closeAvailabilityFormModal = () => {
    this.setState({
      isAvailabilityFormModalOpen: false,
      selectedAvailabilityReport: undefined
    });
  };

  onSubmit = data => {
    const { user } = this.props;
    const { event } = this.state;
    const dataUpdated = {
      ...data,
      playerDetails: {
        permissionId: user.activePermission.id,
        userId: user.userId
      }
    };

    this.setState({
      isLoading: true
    });

    sentStudentAvailabilityReport(user, event.id, dataUpdated)
      .then(() => {
        this.setState(previousState => ({
          isAvailabilityFormModalOpen: false,
          selectedAvailabilityReport: undefined,
          isSuccessModalOpen: true,
          isLoading: false,
          eventContentVersion: previousState.eventContentVersion + 1,
          eventTabToOpen: TABS.AVAILABILITY_REPORTS
        }));
        if (this.props.setNeedUpdate) this.props.setNeedUpdate();
      })
      .catch(error => {
        this.setState({
          isLoading: false,
          isErrorModalOpen: true,
          errorMessage:
            propz.get(error, ['response', 'data', 'details', 'text']) ||
            'Your availability could not be sent. Please try again.'
        });
      });
  };

  renderAvailabilityFormModal = () => {
    const { isAvailabilityFormModalOpen, selectedAvailabilityReport } = this.state;
    return (
      <SimpleModal
        isOpen={isAvailabilityFormModalOpen}
        customClass="parentCompactModal"
        onCloseClick={this.closeAvailabilityFormModal}
      >
        <AvailabilityReportForm
          key={selectedAvailabilityReport?.id || 'new-availability-report'}
          initialReport={selectedAvailabilityReport}
          onCancel={this.closeAvailabilityFormModal}
          onSubmit={this.onSubmit}
        />
      </SimpleModal>
    );
  };

  closeSuccessModal = () => {
    this.setState({
      isSuccessModalOpen: false
    });
  };

  renderSuccessModal() {
    const { isSuccessModalOpen } = this.state;
    return (
      <SimpleModal
        isOpen={isSuccessModalOpen}
        title={'Availability sent'}
        body={'Your availability report has been sent to the school.'}
        buttonCancelText={'Ok'}
        onCloseClick={this.closeSuccessModal}
        customClass={'eChangePasswordTabModal'}
      />
    );
  }

  closeErrorModal = () => this.setState({ isErrorModalOpen: false });

  renderErrorModal() {
    const { isErrorModalOpen, errorMessage } = this.state;
    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title="Availability not sent"
        body={errorMessage}
        buttonCancelText="Close"
        onCloseClick={this.closeErrorModal}
        dialogStyle={{ maxWidth: 520 }}
      />
    );
  }

  emptyFunc = () => {};

  getCallbacks(): EventActionButtonCallbacks {
    return {
      EDIT_EVENT_SETTINGS: this.emptyFunc,
      DUPLICATE_EVENT: this.emptyFunc,
      ADD_SCHOOL: this.emptyFunc,
      ADD_TEAM: this.emptyFunc,
      ADD_RESULTS: this.emptyFunc,
      UPDATE_RESULTS: this.emptyFunc,
      UPDATE_POINTS: this.emptyFunc,
      SEND_CONSENT_REQUEST: this.emptyFunc,
      CANCEL_EVENT: this.emptyFunc,
      CANCEL_EVENT_AND_EDIT_NOTIFICATION_LIST: this.emptyFunc,
      UNCANCEL_EVENT: this.emptyFunc,
      REPORT_AVAILABILITY: this.openAvailabilityModal,
      PRINT: this.emptyFunc,
      DOWNLOAD_CSV: this.emptyFunc,
      DELETE_EVENT: this.emptyFunc,
      IMPORT_RESULTS_FROM_QUANTUM: this.emptyFunc,
      EXPORT_START_LIST_TO_MACSHA_ONE_4_ALL: this.emptyFunc,
      IMPORT_RESULTS_FROM_MACSHA_ONE_4_ALL: this.emptyFunc,
      CLEAN_SCHOOL_LIST: this.emptyFunc,
      SEND_EVENT_NOTIFICATION: this.emptyFunc,
      SEND_EVENT_NOTIFICATION_ELIGIBLE_STUDENTS: this.emptyFunc,
      IMPORT_RESULTS: this.emptyFunc,
      IMPORT_RESULTS_FROM_QUANTUM_AQ: this.emptyFunc,
      CLONE_TOURNAMENT_EVENT: this.emptyFunc,
      ARBITER_BOARD: this.emptyFunc,
      ADD_VIDEO: this.emptyFunc
    };
  }

  renderMapModal() {
    const { isMapModalOpen, event } = this.state;

    const { venue } = event;
    const point = getNewPoint(venue);
    const { coordinates } = point;

    const [latitude, longitude] = coordinates;

    return (
      <SimpleModal isOpen={isMapModalOpen} title={'Venue'} buttonCancelText={'Close'} onCloseClick={this.closeMapModal}>
        <Map point={point} customStylingClass="mb-3 eClubWizardMap" isMarkerDraggable={false} />
        <div>
          <a
            target="_blank"
            href={`http://maps.google.com/maps?q=${longitude},${latitude}+(My+Point)&z=14&ll=${longitude},${latitude}`}
          >
            View on Google Maps
          </a>
        </div>
      </SimpleModal>
    );
  }

  openMapModal = () => {
    this.setState({
      isMapModalOpen: true
    });
  };

  closeMapModal = () => {
    this.setState({
      isMapModalOpen: false
    });
  };

  goBack = () => {
    const { event } = this.state;
    const { history, location } = this.props;
    const state = (location.state || {}) as any;
    const { prevRoutePath = '/calendar', filter } = state;

    const selectedDate = propz.get(event, ['startTime']);

    const isSelectedDateExist = typeof selectedDate !== 'undefined';
    const isFilterExist = typeof filter !== 'undefined';

    const data: any = {
      pathname: prevRoutePath,
      state: {}
    };

    if (isFilterExist) data.state.filter = filter;
    if (isSelectedDateExist) {
      data.state.selectedDate = selectedDate;
    }

    history.push(data);
  };

  render() {
    const { isLoading, prevEvent, nextEvent, event, eventContentVersion, eventTabToOpen } = this.state;
    const { user, location } = this.props;

    const prevRoutePath = propz.get(location, ['state', 'prevRoutePath']);
    const isPrevRoutePathExist = typeof prevRoutePath !== 'undefined' && prevRoutePath !== '';

    if (isLoading) {
      return <Loader />;
    }

    const isPrevEventExist = typeof prevEvent !== 'undefined';
    const isNextEventExist = typeof nextEvent !== 'undefined';

    const eventName = getEventGeneratedName(event, { user });
    const availabilityAction = getActionList(user, event, this.getCallbacks()).find(
      item => item.itemText === EVENT_ACTION_BUTTONS_MAPPING.REPORT_AVAILABILITY
    );
    const isSelfServiceAvailability =
      String(propz.get(user, ['activeSchool', 'settings', 'availabilityWorkflow'], 'SELF_SERVICE')) === 'SELF_SERVICE';
    const canRespondToAvailability = Boolean(availabilityAction) || !isSelfServiceAvailability;

    const classes = 'schoolEventPage';

    return (
      <div className={classes}>
        {this.renderAvailabilityFormModal()}
        {this.renderSuccessModal()}
        {this.renderErrorModal()}
        {this.renderMapModal()}

        <header className="schoolEventPage__header">
          <div className="schoolEventPage__backRow">
            <button type="button" onClick={isPrevRoutePathExist ? this.goBack : () => this.props.history.goBack()}>
              <span aria-hidden="true">←</span> Back to events
            </button>
            <span className="schoolEventPage__backDivider" aria-hidden="true" />
            <strong>{eventName}</strong>
          </div>
          <StudentEventMainInformation
            user={user}
            event={event}
            isPrevButtonDisabled={!isPrevEventExist}
            isNextButtonDisabled={!isNextEventExist}
            onPrevEventClick={this.onPrevEventClick}
            onNextEventClick={this.onNextEventClick}
            onReportAvailabilityClick={availabilityAction && isSelfServiceAvailability ? () => this.openAvailabilityModal() : undefined}
            onViewMapClick={this.openMapModal}
          />
        </header>
        <section className="schoolEventPage__workspace">
          <StudentSummaryTabs
            key={`${event.id}:${eventContentVersion}`}
            user={user}
            eventInitial={event}
            onEditAvailability={canRespondToAvailability ? this.openAvailabilityModal : undefined}
            initialTab={eventTabToOpen}
            initialConversationId={propz.get(this.props.location, ['state', 'conversationId'])}
          />
        </section>
      </div>
    );
  }
}
